// Formulas for the second finance wave (FINANCE_PACK_2_SLUGS). Same engine and
// helpers as finance-pack.ts; kept in its own file so neither grows past a
// reviewable size.

import type { FinanceField, FinanceMeta } from '@/lib/browser-finance-tools';
import { money, payment, payoff, pct, plain, solveRate, yearsMonths } from '@/lib/finance-pack';
import type { FinancePack2Slug } from '@/lib/finance-pack-slugs';

const num = (key: string, label: string, def: string): FinanceField => ({
  key,
  label,
  placeholder: def,
  default: def,
});
const select = (
  key: string,
  label: string,
  def: string,
  options: Array<[string, string]>,
): FinanceField => ({
  key,
  label,
  placeholder: '',
  default: def,
  kind: 'select',
  options: options.map(([l, value]) => ({ label: l, value })),
});

/** Balance left after `paid` payments of `pmt` on a loan at monthly `rate`. */
function balanceAfter(principal: number, rate: number, pmt: number, paid: number): number {
  if (rate === 0) return Math.max(0, principal - pmt * paid);
  const g = (1 + rate) ** paid;
  return Math.max(0, principal * g - (pmt * (g - 1)) / rate);
}

/** Future value of `pmt` paid each period for `n` periods at `rate`; due = start of period. */
function fvAnnuity(pmt: number, rate: number, n: number, due: boolean): number {
  const fv = rate === 0 ? pmt * n : (pmt * ((1 + rate) ** n - 1)) / rate;
  return due ? fv * (1 + rate) : fv;
}
function pvAnnuity(pmt: number, rate: number, n: number, due: boolean): number {
  const pv = rate === 0 ? pmt * n : (pmt * (1 - (1 + rate) ** -n)) / rate;
  return due ? pv * (1 + rate) : pv;
}

/** A plain amortizing loan page with its own defaults (RV, motorcycle, land...). */
function purchaseLoan(
  key: FinancePack2Slug,
  title: string,
  badge: string,
  d: { price: string; down: string; rate: string; months: string },
): FinanceMeta {
  return {
    key,
    title,
    badge,
    fields: [
      num('price', 'Purchase price', d.price),
      num('down', 'Down payment', d.down),
      num('rate', 'Interest rate (APR %)', d.rate),
      num('months', 'Loan term (months)', d.months),
    ],
    compute: ({ price, down, rate, months }) => {
      const loan = Math.max(0, price - down);
      const pmt = payment(loan, rate / 1200, months);
      return [
        { label: 'Monthly payment', value: money(pmt) },
        { label: 'Loan amount', value: money(loan) },
        { label: 'Total interest', value: money(pmt * months - loan) },
        { label: 'Total cost (incl. down payment)', value: money(pmt * months + down) },
      ];
    },
  };
}

// England and Northern Ireland SDLT residential bands from 1 April 2025.
const SDLT_STANDARD: Array<[number, number]> = [
  [125_000, 0],
  [250_000, 0.02],
  [925_000, 0.05],
  [1_500_000, 0.1],
  [Infinity, 0.12],
];
const SDLT_FIRST_TIME: Array<[number, number]> = [
  [300_000, 0],
  [500_000, 0.05],
];
function banded(price: number, bands: Array<[number, number]>, surcharge: number): number {
  let tax = 0;
  let floor = 0;
  for (const [ceiling, rate] of bands) {
    if (price <= floor) break;
    tax += (Math.min(price, ceiling) - floor) * (rate + surcharge);
    floor = ceiling;
  }
  return tax;
}

export const financePack2Meta: Record<FinancePack2Slug, FinanceMeta> = {
  'payment-calculator': {
    key: 'payment-calculator',
    title: 'Payment Calculator',
    badge: 'Payment',
    fields: [
      select('mode', 'Solve for', '0', [
        ['Monthly payment (fixed term)', '0'],
        ['Payoff time (fixed payment)', '1'],
      ]),
      num('loan', 'Loan amount', '25000'),
      num('rate', 'Interest rate (APR %)', '7.5'),
      num('months', 'Term in months (for a fixed term)', '60'),
      num('pmt', 'Monthly payment (for a fixed payment)', '600'),
    ],
    compute: ({ mode, loan, rate, months, pmt }) => {
      const r = rate / 1200;
      if (mode === 1) {
        const p = payoff(loan, r, pmt);
        if (!p) return [{ label: 'Payoff time', value: 'The payment does not cover the interest' }];
        return [
          { label: 'Payoff time', value: `${p.months} months (${yearsMonths(p.months)})` },
          { label: 'Total interest', value: money(p.interest) },
          { label: 'Total paid', value: money(loan + p.interest) },
        ];
      }
      const m = payment(loan, r, months);
      return [
        { label: 'Monthly payment', value: money(m) },
        { label: 'Total interest', value: money(m * months - loan) },
        { label: 'Total paid', value: money(m * months) },
        {
          label: 'Interest share of total',
          value: pct((m * months - loan) / (m * months || 1), 1),
        },
      ];
    },
  },
  'repayment-calculator': {
    key: 'repayment-calculator',
    title: 'Repayment Calculator',
    badge: 'Repayment',
    fields: [
      num('loan', 'Amount owed', '15000'),
      num('rate', 'Interest rate (% per year)', '9'),
      num('pmt', 'Monthly repayment', '350'),
      num('extra', 'Extra monthly repayment', '0'),
    ],
    compute: ({ loan, rate, pmt, extra }) => {
      const r = rate / 1200;
      const base = payoff(loan, r, pmt);
      if (!base)
        return [{ label: 'Time to repay', value: 'The repayment does not cover the interest' }];
      const fast = extra > 0 ? payoff(loan, r, pmt + extra) : null;
      const lines = [
        { label: 'Time to repay', value: `${base.months} months (${yearsMonths(base.months)})` },
        { label: 'Total interest', value: money(base.interest) },
        { label: 'Total repaid', value: money(loan + base.interest) },
      ];
      if (fast)
        lines.push(
          { label: 'With the extra repayment', value: yearsMonths(fast.months) },
          { label: 'Interest saved', value: money(base.interest - fast.interest) },
        );
      return lines;
    },
  },
  'mortgage-overpayment-calculator': {
    key: 'mortgage-overpayment-calculator',
    title: 'Mortgage Overpayment Calculator',
    badge: 'Overpayment',
    fields: [
      num('balance', 'Mortgage balance', '200000'),
      num('rate', 'Interest rate (%)', '4.5'),
      num('years', 'Years remaining', '25'),
      num('monthly', 'Monthly overpayment', '200'),
      num('lump', 'One-off overpayment now', '0'),
    ],
    compute: ({ balance, rate, years, monthly, lump }) => {
      const r = rate / 1200;
      const n = Math.round(years * 12);
      const pmt = payment(balance, r, n);
      const baseInterest = pmt * n - balance;
      const after = payoff(Math.max(0, balance - lump), r, pmt + monthly);
      if (!after) return [{ label: 'New term', value: 'Check the inputs' }];
      return [
        { label: 'Current monthly payment', value: money(pmt) },
        { label: 'New mortgage term', value: yearsMonths(after.months) },
        { label: 'Time saved', value: yearsMonths(Math.max(0, n - after.months)) },
        { label: 'Interest saved', value: money(baseInterest - after.interest) },
        { label: 'Total interest with overpayments', value: money(after.interest) },
      ];
    },
  },
  'mortgage-early-repayment-calculator': {
    key: 'mortgage-early-repayment-calculator',
    title: 'Mortgage Early Repayment Calculator',
    badge: 'Early repayment',
    fields: [
      num('balance', 'Outstanding balance', '150000'),
      num('rate', 'Interest rate (%)', '5'),
      num('years', 'Years remaining', '20'),
      num('erc', 'Early repayment charge (%)', '2'),
    ],
    compute: ({ balance, rate, years, erc }) => {
      const n = Math.round(years * 12);
      const pmt = payment(balance, rate / 1200, n);
      const interestLeft = pmt * n - balance;
      const charge = (balance * erc) / 100;
      return [
        { label: 'Cost to repay today', value: money(balance + charge) },
        { label: 'Early repayment charge', value: money(charge) },
        { label: 'Interest you would still pay', value: money(interestLeft) },
        { label: 'Net saving from repaying now', value: money(interestLeft - charge) },
        { label: 'Current monthly payment', value: money(pmt) },
      ];
    },
  },
  'mortgage-calculator-uk': {
    key: 'mortgage-calculator-uk',
    title: 'Mortgage Calculator UK',
    badge: 'UK mortgage',
    fields: [
      num('price', 'Property price (£)', '300000'),
      num('deposit', 'Deposit (£)', '30000'),
      num('rate', 'Interest rate (%)', '4.5'),
      num('years', 'Mortgage term (years)', '25'),
      select('type', 'Mortgage type', '0', [
        ['Repayment', '0'],
        ['Interest only', '1'],
      ]),
    ],
    compute: ({ price, deposit, rate, years, type }) => {
      const loan = Math.max(0, price - deposit);
      const n = Math.round(years * 12);
      const m = type === 1 ? (loan * rate) / 1200 : payment(loan, rate / 1200, n);
      const interest = type === 1 ? m * n : m * n - loan;
      return [
        { label: 'Monthly payment', value: `£${money(m)}` },
        { label: 'Mortgage amount', value: `£${money(loan)}` },
        { label: 'Loan to value (LTV)', value: pct(loan / (price || 1), 1) },
        { label: 'Total interest', value: `£${money(interest)}` },
        {
          label: type === 1 ? 'Still owed at the end' : 'Total repaid',
          value: `£${money(type === 1 ? loan : m * n)}`,
        },
      ];
    },
  },
  'stamp-duty-calculator': {
    key: 'stamp-duty-calculator',
    title: 'Stamp Duty Calculator',
    badge: 'SDLT',
    fields: [
      num('price', 'Property price (£)', '350000'),
      select('buyer', 'Buyer', '0', [
        ['Moving home (main residence)', '0'],
        ['First-time buyer', '1'],
        ['Additional property / buy-to-let', '2'],
      ]),
      select('overseas', 'Non-UK resident?', '0', [
        ['No', '0'],
        ['Yes (+2%)', '1'],
      ]),
    ],
    compute: ({ price, buyer, overseas }) => {
      const extra = overseas === 1 ? 0.02 : 0;
      const ftbRelief = buyer === 1 && price <= 500_000;
      const tax = ftbRelief
        ? banded(price, SDLT_FIRST_TIME, extra)
        : banded(price, SDLT_STANDARD, extra + (buyer === 2 ? 0.05 : 0));
      const lines = [
        { label: 'Stamp duty to pay', value: `£${money(tax)}` },
        { label: 'Effective rate', value: pct(tax / (price || 1), 2) },
      ];
      if (buyer === 1)
        lines.push({
          label: 'First-time buyer relief',
          value: ftbRelief ? 'Applied' : 'Not available above £500,000',
        });
      return lines;
    },
  },
  'options-profit-calculator': {
    key: 'options-profit-calculator',
    title: 'Options Profit Calculator',
    badge: 'Options',
    fields: [
      select('type', 'Option type', '0', [
        ['Call', '0'],
        ['Put', '1'],
      ]),
      select('side', 'Position', '0', [
        ['Buy (long)', '0'],
        ['Sell (short)', '1'],
      ]),
      num('strike', 'Strike price', '100'),
      num('premium', 'Premium per share', '3.5'),
      num('contracts', 'Contracts (100 shares each)', '1'),
      num('price', 'Stock price at expiration', '110'),
    ],
    compute: ({ type, side, strike, premium, contracts, price }) => {
      const shares = contracts * 100;
      const intrinsic = type === 0 ? Math.max(0, price - strike) : Math.max(0, strike - price);
      const longPl = (intrinsic - premium) * shares;
      const pl = side === 0 ? longPl : -longPl;
      const breakeven = type === 0 ? strike + premium : strike - premium;
      const cost = premium * shares;
      const maxLoss =
        side === 0 ? money(cost) : type === 0 ? 'Unlimited' : money((strike - premium) * shares);
      const maxProfit =
        side === 1 ? money(cost) : type === 0 ? 'Unlimited' : money((strike - premium) * shares);
      return [
        { label: 'Profit / loss at expiration', value: money(pl) },
        { label: 'Return on premium', value: pct(pl / (cost || 1), 1) },
        { label: 'Breakeven stock price', value: money(breakeven) },
        { label: 'Max profit', value: maxProfit },
        { label: 'Max loss', value: maxLoss },
      ];
    },
  },
  'business-loan-calculator': {
    key: 'business-loan-calculator',
    title: 'Business Loan Calculator',
    badge: 'Business loan',
    fields: [
      num('loan', 'Loan amount', '100000'),
      num('rate', 'Interest rate (APR %)', '9.5'),
      num('months', 'Term (months)', '60'),
      num('fee', 'Origination fee (%)', '2'),
    ],
    compute: ({ loan, rate, months, fee }) => {
      const pmt = payment(loan, rate / 1200, months);
      const feeAmount = (loan * fee) / 100;
      const r = solveRate(loan - feeAmount, pmt, months);
      return [
        { label: 'Monthly payment', value: money(pmt) },
        { label: 'Total interest', value: money(pmt * months - loan) },
        { label: 'Origination fee', value: money(feeAmount) },
        { label: 'Cash you receive', value: money(loan - feeAmount) },
        { label: 'Real APR including the fee', value: r === null ? 'n/a' : pct(r * 12, 2) },
      ];
    },
  },
  'fha-loan-calculator': {
    key: 'fha-loan-calculator',
    title: 'FHA Loan Calculator',
    badge: 'FHA',
    fields: [
      num('price', 'Home price', '350000'),
      num('downPct', 'Down payment (%, min 3.5)', '3.5'),
      num('rate', 'Interest rate (%)', '6.5'),
      select('years', 'Loan term', '30', [
        ['30 years', '30'],
        ['15 years', '15'],
      ]),
    ],
    compute: ({ price, downPct, rate, years }) => {
      const down = (price * Math.max(downPct, 3.5)) / 100;
      const base = price - down;
      const ltv = base / (price || 1);
      // HUD annual MIP since March 2023, base loans up to the standard limit.
      const annual = years === 15 ? (ltv > 0.9 ? 0.004 : 0.0015) : ltv > 0.95 ? 0.0055 : 0.005;
      const ufmip = base * 0.0175;
      const loan = base + ufmip;
      const n = years * 12;
      const pi = payment(loan, rate / 1200, n);
      const mip = (base * annual) / 12;
      return [
        { label: 'Monthly payment (P&I + MIP)', value: money(pi + mip) },
        { label: 'Principal and interest', value: money(pi) },
        {
          label: 'Monthly mortgage insurance (MIP)',
          value: `${money(mip)} (${pct(annual, 2)}/yr)`,
        },
        { label: 'Upfront MIP (1.75%, financed)', value: money(ufmip) },
        { label: 'Total loan amount', value: money(loan) },
        { label: 'Down payment', value: money(down) },
      ];
    },
  },
  'va-loan-calculator': {
    key: 'va-loan-calculator',
    title: 'VA Loan Calculator',
    badge: 'VA loan',
    fields: [
      num('price', 'Home price', '400000'),
      num('down', 'Down payment', '0'),
      num('rate', 'Interest rate (%)', '6.25'),
      select('years', 'Loan term', '30', [
        ['30 years', '30'],
        ['15 years', '15'],
      ]),
      select('use', 'VA loan use', '0', [
        ['First use', '0'],
        ['Subsequent use', '1'],
        ['Exempt (service-connected disability)', '2'],
      ]),
    ],
    compute: ({ price, down, rate, years, use }) => {
      const share = down / (price || 1);
      // VA funding fee for purchase loans, effective April 2023.
      const fee =
        use === 2 ? 0 : share >= 0.1 ? 0.0125 : share >= 0.05 ? 0.015 : use === 1 ? 0.033 : 0.0215;
      const base = Math.max(0, price - down);
      const loan = base * (1 + fee);
      const pi = payment(loan, rate / 1200, years * 12);
      return [
        { label: 'Monthly payment (P&I)', value: money(pi) },
        { label: 'VA funding fee', value: `${money(base * fee)} (${pct(fee, 2)})` },
        { label: 'Total loan with fee financed', value: money(loan) },
        { label: 'Total interest', value: money(pi * years * 12 - loan) },
        { label: 'Mortgage insurance', value: 'None on VA loans' },
      ];
    },
  },
  'rv-loan-calculator': purchaseLoan('rv-loan-calculator', 'RV Loan Calculator', 'RV loan', {
    price: '60000',
    down: '6000',
    rate: '8.5',
    months: '180',
  }),
  'motorcycle-loan-calculator': purchaseLoan(
    'motorcycle-loan-calculator',
    'Motorcycle Loan Calculator',
    'Motorcycle',
    { price: '15000', down: '1500', rate: '8', months: '60' },
  ),
  'land-loan-calculator': purchaseLoan(
    'land-loan-calculator',
    'Land Loan Calculator',
    'Land loan',
    {
      price: '100000',
      down: '20000',
      rate: '8.75',
      months: '180',
    },
  ),
  'lease-calculator': {
    key: 'lease-calculator',
    title: 'Lease Calculator',
    badge: 'Lease',
    fields: [
      num('value', 'Price of the item (capitalized cost)', '40000'),
      num('residual', 'Residual value at the end', '24000'),
      num('rate', 'Interest rate (%)', '6'),
      num('months', 'Lease term (months)', '36'),
      num('tax', 'Sales tax on payments (%)', '0'),
    ],
    compute: ({ value, residual, rate, months, tax }) => {
      const depreciation = (value - residual) / (months || 1);
      const moneyFactor = rate / 2400;
      const finance = (value + residual) * moneyFactor;
      const base = depreciation + finance;
      const total = base * (1 + tax / 100);
      return [
        { label: 'Monthly lease payment', value: money(total) },
        { label: 'Depreciation part', value: money(depreciation) },
        { label: 'Finance (rent) charge', value: money(finance) },
        { label: 'Money factor', value: plain(moneyFactor, 5) },
        { label: 'Total of payments', value: money(total * months) },
      ];
    },
  },
  'prorated-rent-calculator': {
    key: 'prorated-rent-calculator',
    title: 'Prorated Rent Calculator',
    badge: 'Prorated rent',
    fields: [
      num('rent', 'Monthly rent', '1500'),
      num('days', 'Days you occupy the unit that month', '12'),
      select('month', 'Days in that month', '30', [
        ['28', '28'],
        ['29', '29'],
        ['30', '30'],
        ['31', '31'],
      ]),
    ],
    compute: ({ rent, days, month }) => {
      const byMonth = (rent / month) * days;
      const byYear = ((rent * 12) / 365) * days;
      const byBanker = (rent / 30) * days;
      return [
        { label: 'Prorated rent (days in month)', value: money(byMonth) },
        { label: 'Daily rate', value: money(rent / month) },
        { label: 'Using the yearly method (÷ 365)', value: money(byYear) },
        { label: "Using a 30-day banker's month", value: money(byBanker) },
      ];
    },
  },
  'gross-profit-margin-calculator': {
    key: 'gross-profit-margin-calculator',
    title: 'Gross Profit Margin Calculator',
    badge: 'Gross margin',
    fields: [num('revenue', 'Revenue', '50000'), num('cogs', 'Cost of goods sold', '30000')],
    compute: ({ revenue, cogs }) => {
      const gp = revenue - cogs;
      return [
        { label: 'Gross profit margin', value: pct(gp / (revenue || 1), 2) },
        { label: 'Gross profit', value: money(gp) },
        { label: 'Markup on cost', value: pct(gp / (cogs || 1), 2) },
        { label: 'COGS share of revenue', value: pct(cogs / (revenue || 1), 2) },
      ];
    },
  },
  'profit-calculator': {
    key: 'profit-calculator',
    title: 'Profit Calculator',
    badge: 'Profit',
    fields: [
      num('cost', 'Cost per unit', '12'),
      num('price', 'Selling price per unit', '20'),
      num('qty', 'Units sold', '500'),
      num('fixed', 'Fixed costs', '1000'),
    ],
    compute: ({ cost, price, qty, fixed }) => {
      const unit = price - cost;
      const profit = unit * qty - fixed;
      return [
        { label: 'Net profit', value: money(profit) },
        { label: 'Profit per unit', value: money(unit) },
        { label: 'Profit margin', value: pct(unit / (price || 1), 2) },
        { label: 'Markup', value: pct(unit / (cost || 1), 2) },
        {
          label: 'Break-even units',
          value: unit > 0 ? plain(Math.ceil(fixed / unit), 0) : 'Price must exceed cost',
        },
      ];
    },
  },
  'net-profit-margin-calculator': {
    key: 'net-profit-margin-calculator',
    title: 'Net Profit Margin Calculator',
    badge: 'Net margin',
    fields: [
      num('revenue', 'Revenue', '200000'),
      num('cogs', 'Cost of goods sold', '90000'),
      num('opex', 'Operating expenses', '60000'),
      num('other', 'Interest and taxes', '15000'),
    ],
    compute: ({ revenue, cogs, opex, other }) => {
      const net = revenue - cogs - opex - other;
      const operating = revenue - cogs - opex;
      return [
        { label: 'Net profit margin', value: pct(net / (revenue || 1), 2) },
        { label: 'Net profit', value: money(net) },
        { label: 'Operating margin', value: pct(operating / (revenue || 1), 2) },
        { label: 'Gross margin', value: pct((revenue - cogs) / (revenue || 1), 2) },
      ];
    },
  },
  'interest-only-mortgage-calculator': {
    key: 'interest-only-mortgage-calculator',
    title: 'Interest-Only Mortgage Calculator',
    badge: 'Interest only',
    fields: [
      num('loan', 'Loan amount', '400000'),
      num('rate', 'Interest rate (%)', '6.5'),
      num('ioYears', 'Interest-only period (years)', '10'),
      num('years', 'Total loan term (years)', '30'),
    ],
    compute: ({ loan, rate, ioYears, years }) => {
      const r = rate / 1200;
      const io = loan * r;
      const rest = Math.max(0, Math.round((years - ioYears) * 12));
      const after = rest > 0 ? payment(loan, r, rest) : 0;
      const total = io * ioYears * 12 + after * rest;
      return [
        { label: 'Interest-only payment', value: money(io) },
        { label: 'Payment after the interest-only period', value: money(after) },
        { label: 'Payment increase', value: money(after - io) },
        { label: 'Total interest', value: money(total - loan) },
        {
          label: `Extra interest vs a normal ${plain(years, 0)}-year loan`,
          value: money(total - payment(loan, r, years * 12) * years * 12),
        },
      ];
    },
  },
  'mortgage-recast-calculator': {
    key: 'mortgage-recast-calculator',
    title: 'Mortgage Recast Calculator',
    badge: 'Recast',
    fields: [
      num('balance', 'Current balance', '300000'),
      num('rate', 'Interest rate (%)', '6'),
      num('months', 'Months remaining', '300'),
      num('lump', 'Lump-sum payment', '50000'),
      num('fee', 'Recast fee', '250'),
    ],
    compute: ({ balance, rate, months, lump, fee }) => {
      const r = rate / 1200;
      const oldPmt = payment(balance, r, months);
      const newBal = Math.max(0, balance - lump);
      const newPmt = payment(newBal, r, months);
      const saved = oldPmt * months - balance - (newPmt * months - newBal);
      return [
        { label: 'New monthly payment', value: money(newPmt) },
        { label: 'Current monthly payment', value: money(oldPmt) },
        { label: 'Monthly savings', value: money(oldPmt - newPmt) },
        { label: 'Interest saved over the loan', value: money(saved - fee) },
        {
          label: 'Months to earn back the fee',
          value: plain(Math.ceil(fee / (oldPmt - newPmt || 1)), 0),
        },
      ];
    },
  },
  'down-payment-calculator': {
    key: 'down-payment-calculator',
    title: 'Down Payment Calculator',
    badge: 'Down payment',
    fields: [
      num('price', 'Home price', '400000'),
      num('downPct', 'Down payment (%)', '20'),
      num('closing', 'Closing costs (% of price)', '3'),
      num('rate', 'Interest rate (%)', '6.5'),
      num('years', 'Loan term (years)', '30'),
    ],
    compute: ({ price, downPct, closing, rate, years }) => {
      const down = (price * downPct) / 100;
      const loan = price - down;
      const cash = down + (price * closing) / 100;
      return [
        { label: 'Down payment', value: money(down) },
        { label: 'Cash needed at closing', value: money(cash) },
        { label: 'Loan amount', value: money(loan) },
        { label: 'Monthly payment (P&I)', value: money(payment(loan, rate / 1200, years * 12)) },
        {
          label: 'Private mortgage insurance',
          value: downPct >= 20 ? 'Not required on a conventional loan' : 'Likely (under 20% down)',
        },
      ];
    },
  },
  'credit-card-payment-calculator': {
    key: 'credit-card-payment-calculator',
    title: 'Credit Card Payment Calculator',
    badge: 'Minimum payment',
    fields: [
      num('balance', 'Card balance', '5000'),
      num('apr', 'APR (%)', '22'),
      num('minPct', 'Minimum payment (% of balance, plus interest)', '1'),
      num('floor', 'Minimum payment floor', '25'),
      num('fixed', 'Or a fixed monthly payment', '250'),
    ],
    compute: ({ balance, apr, minPct, floor, fixed }) => {
      const r = apr / 1200;
      // Common US rule: interest plus a percentage of the balance, never below the floor.
      let b = balance;
      let months = 0;
      let interest = 0;
      const first = Math.min(
        balance * (1 + r),
        Math.max(floor, balance * r + (balance * minPct) / 100),
      );
      while (b > 0.005 && months < 1200) {
        const i = b * r;
        const pmt = Math.min(b + i, Math.max(floor, i + (b * minPct) / 100));
        interest += i;
        b = b + i - pmt;
        months += 1;
      }
      const f = payoff(balance, r, fixed);
      return [
        { label: 'First minimum payment', value: money(first) },
        {
          label: 'Paying only the minimum',
          value: `${yearsMonths(months)}, ${money(interest)} interest`,
        },
        {
          label: `Paying ${money(fixed)} a month`,
          value: f
            ? `${yearsMonths(f.months)}, ${money(f.interest)} interest`
            : 'Does not cover the interest',
        },
      ];
    },
  },
  'car-loan-payoff-calculator': {
    key: 'car-loan-payoff-calculator',
    title: 'Car Loan Payoff Calculator',
    badge: 'Car payoff',
    fields: [
      num('balance', 'Remaining balance', '18000'),
      num('rate', 'Interest rate (APR %)', '7'),
      num('pmt', 'Current monthly payment', '450'),
      num('extra', 'Extra per month', '100'),
    ],
    compute: ({ balance, rate, pmt, extra }) => {
      const r = rate / 1200;
      const base = payoff(balance, r, pmt);
      const fast = payoff(balance, r, pmt + extra);
      if (!base || !fast)
        return [{ label: 'Payoff', value: 'The payment does not cover the interest' }];
      return [
        { label: 'Payoff time now', value: yearsMonths(base.months) },
        { label: 'Payoff time with extra', value: yearsMonths(fast.months) },
        { label: 'Months saved', value: plain(base.months - fast.months, 0) },
        { label: 'Interest saved', value: money(base.interest - fast.interest) },
        { label: 'Interest left to pay (current plan)', value: money(base.interest) },
      ];
    },
  },
  'depreciation-calculator': {
    key: 'depreciation-calculator',
    title: 'Depreciation Calculator',
    badge: 'Depreciation',
    fields: [
      num('cost', 'Asset cost', '50000'),
      num('salvage', 'Salvage value', '5000'),
      num('life', 'Useful life (years)', '5'),
      num('year', 'Show year', '1'),
      select('method', 'Method', '0', [
        ['Straight line', '0'],
        ['Double declining balance', '1'],
        ["Sum of the years' digits", '2'],
      ]),
    ],
    compute: ({ cost, salvage, life, year, method }) => {
      const n = Math.max(1, Math.round(life));
      const y = Math.min(n, Math.max(1, Math.round(year)));
      const base = cost - salvage;
      let book = cost;
      let dep = 0;
      for (let k = 1; k <= y; k += 1) {
        if (method === 1) {
          dep = k === n ? book - salvage : Math.min(book * (2 / n), Math.max(0, book - salvage));
        } else if (method === 2) {
          dep = (base * (n - k + 1)) / ((n * (n + 1)) / 2);
        } else dep = base / n;
        book -= dep;
      }
      return [
        { label: `Depreciation in year ${y}`, value: money(dep) },
        { label: `Book value at end of year ${y}`, value: money(book) },
        { label: 'Accumulated depreciation', value: money(cost - book) },
        { label: 'Total depreciable amount', value: money(base) },
      ];
    },
  },
  'straight-line-depreciation-calculator': {
    key: 'straight-line-depreciation-calculator',
    title: 'Straight Line Depreciation Calculator',
    badge: 'Straight line',
    fields: [
      num('cost', 'Asset cost', '12000'),
      num('salvage', 'Salvage value', '2000'),
      num('life', 'Useful life (years)', '5'),
    ],
    compute: ({ cost, salvage, life }) => {
      const annual = (cost - salvage) / (life || 1);
      return [
        { label: 'Annual depreciation', value: money(annual) },
        { label: 'Monthly depreciation', value: money(annual / 12) },
        { label: 'Depreciation rate', value: pct(annual / (cost || 1), 2) },
        { label: 'Total depreciation', value: money(cost - salvage) },
      ];
    },
  },
  'car-depreciation-calculator': {
    key: 'car-depreciation-calculator',
    title: 'Car Depreciation Calculator',
    badge: 'Car value',
    fields: [
      num('price', 'Price when new', '40000'),
      num('years', 'Age of the car (years)', '3'),
      num('first', 'First-year loss (%)', '20'),
      num('later', 'Yearly loss after that (%)', '15'),
    ],
    compute: ({ price, years, first, later }) => {
      const y = Math.max(0, years);
      const value =
        y <= 1
          ? price * (1 - (first / 100) * y)
          : price * (1 - first / 100) * (1 - later / 100) ** (y - 1);
      return [
        { label: 'Estimated value now', value: money(value) },
        { label: 'Value lost', value: money(price - value) },
        { label: 'Share of the price kept', value: pct(value / (price || 1), 1) },
        {
          label: 'Value in 5 years from new',
          value: money(price * (1 - first / 100) * (1 - later / 100) ** 4),
        },
      ];
    },
  },
  'annuity-payout-calculator': {
    key: 'annuity-payout-calculator',
    title: 'Annuity Payout Calculator',
    badge: 'Payout',
    fields: [
      num('balance', 'Starting balance', '500000'),
      num('rate', 'Annual return (%)', '5'),
      num('years', 'Payout period (years)', '25'),
      select('freq', 'Payout frequency', '12', [
        ['Monthly', '12'],
        ['Quarterly', '4'],
        ['Yearly', '1'],
      ]),
    ],
    compute: ({ balance, rate, years, freq }) => {
      const n = Math.round(years * freq);
      const r = rate / 100 / freq;
      const pmt = payment(balance, r, n);
      return [
        { label: 'Payout each period', value: money(pmt) },
        { label: 'Total paid out', value: money(pmt * n) },
        { label: 'Of which growth', value: money(pmt * n - balance) },
        { label: 'Interest-only payout (balance kept)', value: money(balance * r) },
      ];
    },
  },
  'rental-yield-calculator': {
    key: 'rental-yield-calculator',
    title: 'Rental Yield Calculator',
    badge: 'Yield',
    fields: [
      num('price', 'Property price', '250000'),
      num('rent', 'Monthly rent', '1400'),
      num('costs', 'Annual costs (tax, insurance, repairs, fees)', '3500'),
      num('vacancy', 'Vacancy (%)', '5'),
    ],
    compute: ({ price, rent, costs, vacancy }) => {
      const gross = rent * 12;
      const net = gross * (1 - vacancy / 100) - costs;
      return [
        { label: 'Gross rental yield', value: pct(gross / (price || 1), 2) },
        { label: 'Net rental yield', value: pct(net / (price || 1), 2) },
        { label: 'Annual rent', value: money(gross) },
        { label: 'Net annual income', value: money(net) },
      ];
    },
  },
  'rental-property-calculator': {
    key: 'rental-property-calculator',
    title: 'Rental Property Calculator',
    badge: 'Rental',
    fields: [
      num('price', 'Purchase price', '300000'),
      num('downPct', 'Down payment (%)', '25'),
      num('rate', 'Mortgage rate (%)', '7'),
      num('rent', 'Monthly rent', '2400'),
      num('expenses', 'Monthly expenses (tax, insurance, HOA, repairs)', '600'),
      num('vacancy', 'Vacancy (%)', '5'),
    ],
    compute: ({ price, downPct, rate, rent, expenses, vacancy }) => {
      const down = (price * downPct) / 100;
      const mortgage = payment(price - down, rate / 1200, 360);
      const effective = rent * (1 - vacancy / 100);
      const noi = (effective - expenses) * 12;
      const cash = effective - expenses - mortgage;
      return [
        { label: 'Monthly cash flow', value: money(cash) },
        { label: 'Cap rate', value: pct(noi / (price || 1), 2) },
        { label: 'Cash-on-cash return', value: pct((cash * 12) / (down || 1), 2) },
        { label: 'Net operating income (yearly)', value: money(noi) },
        { label: 'Mortgage payment (30 yr)', value: money(mortgage) },
      ];
    },
  },
  'construction-loan-calculator': {
    key: 'construction-loan-calculator',
    title: 'Construction Loan Calculator',
    badge: 'Construction',
    fields: [
      num('loan', 'Construction loan amount', '400000'),
      num('rate', 'Interest rate during build (%)', '8.5'),
      num('months', 'Build time (months)', '12'),
      num('drawn', 'Average share drawn during the build (%)', '50'),
      num('permRate', 'Permanent mortgage rate (%)', '7'),
    ],
    compute: ({ loan, rate, months, drawn, permRate }) => {
      const monthlyAvg = (loan * (drawn / 100) * rate) / 1200;
      return [
        { label: 'Average interest-only payment during the build', value: money(monthlyAvg) },
        { label: 'Interest-only payment when fully drawn', value: money((loan * rate) / 1200) },
        { label: 'Total interest during the build', value: money(monthlyAvg * months) },
        {
          label: 'Mortgage payment after (30 yr)',
          value: money(payment(loan, permRate / 1200, 360)),
        },
      ];
    },
  },
  'savings-goal-calculator': {
    key: 'savings-goal-calculator',
    title: 'Savings Goal Calculator',
    badge: 'Savings goal',
    fields: [
      num('goal', 'Savings goal', '20000'),
      num('current', 'Already saved', '2000'),
      num('rate', 'Interest rate (% per year)', '4'),
      num('years', 'Years to reach the goal', '3'),
    ],
    compute: ({ goal, current, rate, years }) => {
      const r = rate / 1200;
      const n = Math.round(years * 12);
      const grown = current * (1 + r) ** n;
      const need = Math.max(0, goal - grown);
      const monthly = r === 0 ? need / (n || 1) : (need * r) / ((1 + r) ** n - 1);
      return [
        { label: 'Save each month', value: money(monthly) },
        { label: 'Total you deposit', value: money(monthly * n + current) },
        { label: 'Interest earned', value: money(goal - monthly * n - current) },
        { label: 'Weekly equivalent', value: money((monthly * 12) / 52) },
      ];
    },
  },
  'credit-utilization-calculator': {
    key: 'credit-utilization-calculator',
    title: 'Credit Utilization Calculator',
    badge: 'Utilization',
    fields: [
      num('b1', 'Card 1 balance', '1200'),
      num('l1', 'Card 1 limit', '5000'),
      num('b2', 'Card 2 balance', '800'),
      num('l2', 'Card 2 limit', '3000'),
      num('b3', 'Card 3 balance', '0'),
      num('l3', 'Card 3 limit', '0'),
    ],
    compute: ({ b1, l1, b2, l2, b3, l3 }) => {
      const bal = b1 + b2 + b3;
      const lim = l1 + l2 + l3;
      const u = bal / (lim || 1);
      const band =
        u < 0.1
          ? 'Excellent (under 10%)'
          : u < 0.3
            ? 'Good (under 30%)'
            : u < 0.5
              ? 'Fair'
              : 'High';
      const highest = Math.max(l1 ? b1 / l1 : 0, l2 ? b2 / l2 : 0, l3 ? b3 / l3 : 0);
      return [
        { label: 'Overall utilization', value: pct(u, 1) },
        { label: 'Rating', value: band },
        { label: 'Highest single card', value: pct(highest, 1) },
        { label: 'Balance to stay under 30%', value: money(lim * 0.3) },
        { label: 'Pay down to reach 10%', value: money(Math.max(0, bal - lim * 0.1)) },
      ];
    },
  },
  'dividend-reinvestment-calculator': {
    key: 'dividend-reinvestment-calculator',
    title: 'Dividend Reinvestment Calculator',
    badge: 'DRIP',
    fields: [
      num('initial', 'Initial investment', '10000'),
      num('monthly', 'Monthly contribution', '200'),
      num('yield', 'Dividend yield (%)', '3.5'),
      num('growth', 'Share price growth (% per year)', '5'),
      num('years', 'Years', '20'),
    ],
    compute: ({ initial, monthly, yield: y, growth, years }) => {
      const run = (reinvest: boolean) => {
        let value = initial;
        let cash = 0;
        for (let m = 0; m < Math.round(years * 12); m += 1) {
          const div = (value * y) / 1200;
          value = value * (1 + growth / 100) ** (1 / 12) + monthly + (reinvest ? div : 0);
          if (!reinvest) cash += div;
        }
        return { value, cash };
      };
      const drip = run(true);
      const noDrip = run(false);
      return [
        { label: 'Value with dividends reinvested', value: money(drip.value) },
        { label: 'Value without reinvesting', value: money(noDrip.value) },
        { label: 'Dividends taken as cash (no DRIP)', value: money(noDrip.cash) },
        { label: 'Yearly dividend income at the end (DRIP)', value: money((drip.value * y) / 100) },
        { label: 'Total contributed', value: money(initial + monthly * Math.round(years * 12)) },
      ];
    },
  },
  'dividend-yield-calculator': {
    key: 'dividend-yield-calculator',
    title: 'Dividend Yield Calculator',
    badge: 'Yield',
    fields: [
      num('dividend', 'Dividend per share (per payment)', '0.5'),
      select('freq', 'Payments per year', '4', [
        ['Quarterly', '4'],
        ['Monthly', '12'],
        ['Semi-annual', '2'],
        ['Annual', '1'],
      ]),
      num('price', 'Share price', '60'),
      num('shares', 'Shares owned', '100'),
    ],
    compute: ({ dividend, freq, price, shares }) => {
      const annual = dividend * freq;
      return [
        { label: 'Dividend yield', value: pct(annual / (price || 1), 2) },
        { label: 'Annual dividend per share', value: money(annual) },
        { label: 'Your yearly dividend income', value: money(annual * shares) },
        { label: 'Per payment', value: money(dividend * shares) },
      ];
    },
  },
  'debt-consolidation-calculator': {
    key: 'debt-consolidation-calculator',
    title: 'Debt Consolidation Calculator',
    badge: 'Consolidation',
    fields: [
      num('balance', 'Total debt to consolidate', '20000'),
      num('apr', 'Current average APR (%)', '22'),
      num('pmt', 'Current total monthly payments', '600'),
      num('newApr', 'New loan APR (%)', '11'),
      num('months', 'New loan term (months)', '48'),
      num('fee', 'Origination fee (%)', '3'),
    ],
    compute: ({ balance, apr, pmt, newApr, months, fee }) => {
      const now = payoff(balance, apr / 1200, pmt);
      const loan = balance / (1 - fee / 100);
      const newPmt = payment(loan, newApr / 1200, months);
      const newCost = newPmt * months - balance;
      return [
        { label: 'New monthly payment', value: money(newPmt) },
        { label: 'Total cost of the new loan (interest + fee)', value: money(newCost) },
        {
          label: 'Interest on current debts',
          value: now
            ? `${money(now.interest)} over ${yearsMonths(now.months)}`
            : 'Payments do not cover the interest',
        },
        {
          label: 'Saving from consolidating',
          value: now ? money(now.interest - newCost) : 'n/a',
        },
      ];
    },
  },
  'mortgage-points-calculator': {
    key: 'mortgage-points-calculator',
    title: 'Mortgage Points Calculator',
    badge: 'Points',
    fields: [
      num('loan', 'Loan amount', '350000'),
      num('rate', 'Rate without points (%)', '7'),
      num('points', 'Points bought', '2'),
      num('pointRate', 'Rate with points (%)', '6.5'),
      num('years', 'Loan term (years)', '30'),
    ],
    compute: ({ loan, rate, points, pointRate, years }) => {
      const n = years * 12;
      const a = payment(loan, rate / 1200, n);
      const b = payment(loan, pointRate / 1200, n);
      const cost = (loan * points) / 100;
      const saving = a - b;
      return [
        { label: 'Cost of the points', value: money(cost) },
        { label: 'Monthly saving', value: money(saving) },
        {
          label: 'Break-even',
          value: saving > 0 ? yearsMonths(Math.ceil(cost / saving)) : 'Never',
        },
        { label: 'Net saving over the full term', value: money(saving * n - cost) },
      ];
    },
  },
  'balloon-loan-calculator': {
    key: 'balloon-loan-calculator',
    title: 'Balloon Loan Calculator',
    badge: 'Balloon',
    fields: [
      num('loan', 'Loan amount', '200000'),
      num('rate', 'Interest rate (%)', '7'),
      num('amort', 'Amortization period (years)', '30'),
      num('balloon', 'Balloon due after (years)', '7'),
    ],
    compute: ({ loan, rate, amort, balloon }) => {
      const r = rate / 1200;
      const pmt = payment(loan, r, amort * 12);
      const k = Math.round(balloon * 12);
      const left = balanceAfter(loan, r, pmt, k);
      return [
        { label: 'Monthly payment', value: money(pmt) },
        { label: 'Balloon payment due', value: money(left) },
        { label: 'Interest paid before the balloon', value: money(pmt * k - (loan - left)) },
        { label: 'Principal repaid before the balloon', value: money(loan - left) },
      ];
    },
  },
  'present-value-of-annuity-calculator': {
    key: 'present-value-of-annuity-calculator',
    title: 'Present Value of Annuity Calculator',
    badge: 'PV annuity',
    fields: [
      num('pmt', 'Payment per period', '1000'),
      num('rate', 'Interest rate per period (%)', '5'),
      num('n', 'Number of periods', '10'),
      select('due', 'Payments at', '0', [
        ['End of each period (ordinary)', '0'],
        ['Start of each period (annuity due)', '1'],
      ]),
    ],
    compute: ({ pmt, rate, n, due }) => {
      const pv = pvAnnuity(pmt, rate / 100, n, due === 1);
      return [
        { label: 'Present value', value: money(pv) },
        { label: 'Total of payments', value: money(pmt * n) },
        { label: 'Discount (interest)', value: money(pmt * n - pv) },
      ];
    },
  },
  'future-value-of-annuity-calculator': {
    key: 'future-value-of-annuity-calculator',
    title: 'Future Value of Annuity Calculator',
    badge: 'FV annuity',
    fields: [
      num('pmt', 'Payment per period', '1000'),
      num('rate', 'Interest rate per period (%)', '5'),
      num('n', 'Number of periods', '10'),
      select('due', 'Payments at', '0', [
        ['End of each period (ordinary)', '0'],
        ['Start of each period (annuity due)', '1'],
      ]),
    ],
    compute: ({ pmt, rate, n, due }) => {
      const fv = fvAnnuity(pmt, rate / 100, n, due === 1);
      return [
        { label: 'Future value', value: money(fv) },
        { label: 'Total of payments', value: money(pmt * n) },
        { label: 'Interest earned', value: money(fv - pmt * n) },
      ];
    },
  },
  'current-ratio-calculator': {
    key: 'current-ratio-calculator',
    title: 'Current Ratio Calculator',
    badge: 'Liquidity',
    fields: [
      num('assets', 'Current assets', '250000'),
      num('liabilities', 'Current liabilities', '125000'),
      num('inventory', 'Inventory (for the quick ratio)', '60000'),
      num('cash', 'Cash and equivalents', '40000'),
    ],
    compute: ({ assets, liabilities, inventory, cash }) => [
      { label: 'Current ratio', value: plain(assets / (liabilities || 1), 2) },
      { label: 'Quick ratio', value: plain((assets - inventory) / (liabilities || 1), 2) },
      { label: 'Cash ratio', value: plain(cash / (liabilities || 1), 2) },
      { label: 'Working capital', value: money(assets - liabilities) },
    ],
  },
  'tax-equivalent-yield-calculator': {
    key: 'tax-equivalent-yield-calculator',
    title: 'Tax Equivalent Yield Calculator',
    badge: 'TEY',
    fields: [
      num('yield', 'Tax-free yield (%)', '3.5'),
      num('federal', 'Federal tax bracket (%)', '24'),
      num('state', 'State tax rate (%, if also exempt)', '0'),
    ],
    compute: ({ yield: y, federal, state }) => {
      const combined = federal / 100 + (state / 100) * (1 - federal / 100);
      return [
        { label: 'Tax-equivalent yield', value: pct(y / 100 / (1 - combined || 1), 2) },
        { label: 'Combined tax rate used', value: pct(combined, 2) },
      ];
    },
  },
  'discounted-cash-flow-calculator': {
    key: 'discounted-cash-flow-calculator',
    title: 'Discounted Cash Flow Calculator',
    badge: 'DCF',
    fields: [
      num('fcf', 'Free cash flow this year', '1000000'),
      num('growth', 'Growth rate, years 1-5 (%)', '10'),
      num('discount', 'Discount rate (%)', '9'),
      num('terminal', 'Terminal growth rate (%)', '2.5'),
      num('shares', 'Shares outstanding (optional)', '0'),
      num('debt', 'Net debt (optional)', '0'),
    ],
    compute: ({ fcf, growth, discount, terminal, shares, debt }) => {
      const d = discount / 100;
      let pv = 0;
      let cf = fcf;
      for (let y = 1; y <= 5; y += 1) {
        cf *= 1 + growth / 100;
        pv += cf / (1 + d) ** y;
      }
      if (d <= terminal / 100)
        return [{ label: 'Value', value: 'Discount rate must exceed terminal growth' }];
      const tv = (cf * (1 + terminal / 100)) / (d - terminal / 100);
      const pvTv = tv / (1 + d) ** 5;
      const ev = pv + pvTv;
      const lines = [
        { label: 'Enterprise value', value: money(ev) },
        { label: 'PV of 5-year cash flows', value: money(pv) },
        { label: 'PV of terminal value', value: `${money(pvTv)} (${pct(pvTv / ev, 1)} of total)` },
      ];
      if (shares > 0) lines.push({ label: 'Value per share', value: money((ev - debt) / shares) });
      return lines;
    },
  },
  'roic-calculator': {
    key: 'roic-calculator',
    title: 'ROIC Calculator',
    badge: 'ROIC',
    fields: [
      num('ebit', 'Operating income (EBIT)', '500000'),
      num('tax', 'Tax rate (%)', '21'),
      num('debt', 'Total debt', '1000000'),
      num('equity', 'Shareholder equity', '2000000'),
      num('cash', 'Cash (excluded from capital)', '200000'),
    ],
    compute: ({ ebit, tax, debt, equity, cash }) => {
      const nopat = ebit * (1 - tax / 100);
      const capital = debt + equity - cash;
      return [
        { label: 'ROIC', value: pct(nopat / (capital || 1), 2) },
        { label: 'NOPAT', value: money(nopat) },
        { label: 'Invested capital', value: money(capital) },
      ];
    },
  },
  'rent-increase-calculator': {
    key: 'rent-increase-calculator',
    title: 'Rent Increase Calculator',
    badge: 'Rent increase',
    fields: [
      num('rent', 'Current monthly rent', '1800'),
      num('increase', 'Increase (%)', '5'),
      num('years', 'Repeat for years (optional)', '1'),
    ],
    compute: ({ rent, increase, years }) => {
      const next = rent * (1 + increase / 100);
      const later = rent * (1 + increase / 100) ** Math.max(1, years);
      return [
        { label: 'New monthly rent', value: money(next) },
        { label: 'Monthly increase', value: money(next - rent) },
        { label: 'Extra per year', value: money((next - rent) * 12) },
        {
          label: `Rent after ${plain(Math.max(1, years), 0)} year(s) of increases`,
          value: money(later),
        },
      ];
    },
  },
  'times-interest-earned-ratio-calculator': {
    key: 'times-interest-earned-ratio-calculator',
    title: 'Times Interest Earned Ratio Calculator',
    badge: 'TIE',
    fields: [
      num('ebit', 'Operating income (EBIT)', '400000'),
      num('interest', 'Interest expense', '80000'),
    ],
    compute: ({ ebit, interest }) => {
      const tie = ebit / (interest || 1);
      return [
        { label: 'Times interest earned', value: `${plain(tie, 2)}×` },
        {
          label: 'Reading',
          value:
            tie >= 3
              ? 'Comfortable (3× or more)'
              : tie >= 1.5
                ? 'Adequate'
                : 'Weak: interest barely covered',
        },
      ];
    },
  },
  'apr-to-apy-calculator': {
    key: 'apr-to-apy-calculator',
    title: 'APR to APY Calculator',
    badge: 'APR → APY',
    fields: [
      num('apr', 'APR (%)', '5'),
      select('n', 'Compounding', '12', [
        ['Daily', '365'],
        ['Monthly', '12'],
        ['Quarterly', '4'],
        ['Semi-annually', '2'],
        ['Annually', '1'],
      ]),
    ],
    compute: ({ apr, n }) => [
      { label: 'APY', value: pct((1 + apr / 100 / n) ** n - 1, 3) },
      { label: 'Continuous compounding APY', value: pct(Math.exp(apr / 100) - 1, 3) },
    ],
  },
  'apy-to-apr-calculator': {
    key: 'apy-to-apr-calculator',
    title: 'APY to APR Calculator',
    badge: 'APY → APR',
    fields: [
      num('apy', 'APY (%)', '5'),
      select('n', 'Compounding', '12', [
        ['Daily', '365'],
        ['Monthly', '12'],
        ['Quarterly', '4'],
        ['Semi-annually', '2'],
        ['Annually', '1'],
      ]),
    ],
    compute: ({ apy, n }) => [
      { label: 'APR', value: pct(n * ((1 + apy / 100) ** (1 / n) - 1), 3) },
      { label: 'Rate per period', value: pct((1 + apy / 100) ** (1 / n) - 1, 4) },
    ],
  },
  'debt-to-equity-ratio-calculator': {
    key: 'debt-to-equity-ratio-calculator',
    title: 'Debt to Equity Ratio Calculator',
    badge: 'D/E',
    fields: [
      num('liabilities', 'Total liabilities', '600000'),
      num('equity', 'Shareholder equity', '400000'),
    ],
    compute: ({ liabilities, equity }) => [
      { label: 'Debt-to-equity ratio', value: plain(liabilities / (equity || 1), 2) },
      {
        label: 'Debt ratio (liabilities ÷ assets)',
        value: pct(liabilities / (liabilities + equity || 1), 1),
      },
      { label: 'Equity ratio', value: pct(equity / (liabilities + equity || 1), 1) },
    ],
  },
  'fuel-savings-calculator': {
    key: 'fuel-savings-calculator',
    title: 'Fuel Savings Calculator',
    badge: 'Fuel',
    fields: [
      num('miles', 'Miles driven per year', '12000'),
      num('oldMpg', 'Current car MPG', '24'),
      num('newMpg', 'New car MPG', '38'),
      num('price', 'Fuel price per gallon', '3.4'),
    ],
    compute: ({ miles, oldMpg, newMpg, price }) => {
      const a = (miles / (oldMpg || 1)) * price;
      const b = (miles / (newMpg || 1)) * price;
      return [
        { label: 'Savings per year', value: money(a - b) },
        { label: 'Savings over 5 years', value: money((a - b) * 5) },
        { label: 'Fuel cost now (yearly)', value: money(a) },
        { label: 'Fuel cost with the new car', value: money(b) },
        {
          label: 'Gallons saved per year',
          value: plain(miles / (oldMpg || 1) - miles / (newMpg || 1), 0),
        },
      ];
    },
  },
  'forex-margin-calculator': {
    key: 'forex-margin-calculator',
    title: 'Forex Margin Calculator',
    badge: 'Margin',
    fields: [
      num('lots', 'Trade size (lots)', '1'),
      num('price', 'Exchange rate (quote per base)', '1.085'),
      num('leverage', 'Leverage (e.g. 30 for 30:1)', '30'),
      num('balance', 'Account balance (quote currency)', '10000'),
    ],
    compute: ({ lots, price, leverage, balance }) => {
      const notional = lots * 100_000 * price;
      const margin = notional / (leverage || 1);
      return [
        { label: 'Required margin', value: money(margin) },
        { label: 'Position value (notional)', value: money(notional) },
        { label: 'Margin as % of balance', value: pct(margin / (balance || 1), 1) },
        { label: 'Free margin', value: money(balance - margin) },
        { label: 'Pip value (4-decimal pair)', value: money(lots * 100_000 * 0.0001) },
      ];
    },
  },
};
